/* eslint-disable @typescript-eslint/no-non-null-assertion */
import axios, { AxiosResponse } from 'axios'
import { emptyString } from '../../../../constant/constantData'
import { withDefault } from '../../../../pages/Recruitment/CandidateList/CandidateListHelpers'
import {
  InterviewCandidateListResponseType,
  GetInterviewCandidateListType,
  InterviewCandidateListConfigType,
} from '../../../../types/Recruitment/CandidateList/CandidateInterviewListTypes'
import {
  AddNewCandidateDTO,
  CandidateListTableProps,
  CandidateViewTotalInfo,
  EmployeeListItem,
  GetAllJobVacanciesParams,
  GetAllStatus,
  GetAllTechnology,
  IncomingAllJobVacanciesList,
  IncomingCompaniesData,
  IncomingEditCandidateData,
  IsEditNewCandidateEmailExistsParams,
  IsEditNewCandidateMobileNumExistsParams,
  UploadCandidateResumeDTO,
  UploadResumeType,
  country,
  exportCandidateListProps,
  recruitersDetails,
  viewHandlerProps,
} from '../../../../types/Recruitment/CandidateList/CandidateListTypes'
import {
  getAuthenticatedRequestConfig,
  useAxios,
} from '../../../../utils/apiUtils'
import { AllowedHttpMethods, CandidateListApiConfig } from '../../apiList'

const searchScheduledCandidate = async (
  props: CandidateListTableProps,
): Promise<CandidateViewTotalInfo> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.searchScheduledCandidate,
    method: AllowedHttpMethods.get,
    params: {
      searchStr: props.searchStr,
      endIndex: props.endIndex,
      startIndex: props.startIndex,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getEmpCountries = async (): Promise<country[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getEmpCountries,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getTechnology = async (): Promise<GetAllTechnology[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getAllTechnology,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}
const getStatus = async (): Promise<GetAllStatus[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getAllStatus,
    method: AllowedHttpMethods.get,
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getCountryWiseCandidatesList = async (
  data: viewHandlerProps,
): Promise<CandidateViewTotalInfo> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getCountryWiseCandidatesList,
    method: AllowedHttpMethods.post,
    data,
  })

  const response = await useAxios(requestConfig)
  return response.data
}
const deleteCandidate = async (candidateId: number): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.deleteCandidate,
    method: AllowedHttpMethods.get,
    params: {
      candidateId,
    },
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getAllJobVacancies = async (
  params: GetAllJobVacanciesParams,
): Promise<IncomingAllJobVacanciesList> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getAllJobVacanciesList,
    method: AllowedHttpMethods.get,
    params: {
      endIndex: params.endIndex ?? '',
      startIndex: params.startIndex ?? '',
      searchJobTitle: params.searchJobTitle ?? '',
      status: params.status ?? 'open',
      vendorEmployeeId: localStorage.getItem('vendorEmployeeId') ?? '',
      isVendor: localStorage.getItem('isVendor') === 'true',
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getAllEmployeeDetails = async (): Promise<EmployeeListItem[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getAllEmployeeDetails,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const checkCandidateEmail = async (email: string): Promise<boolean> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.checkCandidateEmail,
    method: AllowedHttpMethods.get,
    params: {
      email,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const checkCandidateMobileNumber = async (
  candidateMobileNumber: string,
): Promise<boolean> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.checkCandidateMobileNumber,
    method: AllowedHttpMethods.get,
    params: {
      candidateMobileNumber,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getCandidateIdByEmail = async (email: string): Promise<number | null> => {
  try {
    // First check if candidate exists
    const exists = await checkCandidateEmail(email)
    if (!exists) {
      return null
    }

    // Search for the candidate to get their ID
    const searchResult = await searchScheduledCandidate({
      searchStr: email,
      startIndex: 0,
      endIndex: 10,
    })

    // Find the candidate with matching email
    const candidate = searchResult.list?.find(
      (c) => c.email?.toLowerCase() === email.toLowerCase(),
    )

    return candidate?.personId || null
  } catch (error) {
    return null
  }
}

const getCandidateIdByPhone = async (
  candidateMobileNumber: string,
): Promise<number | null> => {
  try {
    // First check if candidate exists
    const exists = await checkCandidateMobileNumber(candidateMobileNumber)
    if (!exists) {
      return null
    }

    // Search for the candidate to get their ID
    const searchResult = await searchScheduledCandidate({
      searchStr: candidateMobileNumber,
      startIndex: 0,
      endIndex: 10,
    })

    // Find the candidate with matching phone (check both mobile and phone fields)
    const candidate = searchResult.list?.find((c) =>
      c.mobile?.includes(candidateMobileNumber),
    )

    return candidate?.personId || null
  } catch (error) {
    return null
  }
}

const getAllCompaniesData = async (): Promise<IncomingCompaniesData[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getAllCompaniesData,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const addNewCandidate = async (
  finalData: AddNewCandidateDTO,
): Promise<number> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.addNewCandidate,
    method: AllowedHttpMethods.post,
    data: finalData,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const uploadCandidateResume = async (
  finalData: UploadCandidateResumeDTO,
): Promise<undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.uploadCandidateResume,
    method: AllowedHttpMethods.post,
    params: {
      personId: finalData.personId,
    },
    data: finalData.file,
    additionalHeaders: {
      'Content-Type': 'multipart/form-data',
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const addTechnology = async (technology: string): Promise<undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.addTechnology,
    method: AllowedHttpMethods.post,
    params: {
      technology,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const deleteTechnology = async (technologyId: number): Promise<undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.deleteTechnology,
    method: AllowedHttpMethods.get,
    params: {
      technologyId,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const editCandidateData = async (
  candidateId: number,
): Promise<IncomingEditCandidateData> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.editCandidateData,
    method: AllowedHttpMethods.get,
    params: {
      candidateId,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const isEditNewCandidateEmailExists = async (
  finalParams: IsEditNewCandidateEmailExistsParams,
): Promise<boolean> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.isEditCandidateMailExists,
    method: AllowedHttpMethods.get,
    params: {
      ...finalParams,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const isEditNewCandidateMobileNumExists = async (
  finalParams: IsEditNewCandidateMobileNumExistsParams,
): Promise<boolean> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.isEditCandidateMobileNumberExists,
    method: AllowedHttpMethods.get,
    params: {
      ...finalParams,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const editNewCandidate = async (
  finalData: IncomingEditCandidateData,
): Promise<undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.editNewCandidate,
    method: AllowedHttpMethods.post,
    data: {
      ...finalData,
    },
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const downloadFile = async (fileName: string): Promise<Blob> => {
  const tenantKey = localStorage.getItem('tenantKey')!
  const token = localStorage.getItem('token')!
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.downloadFile,
    method: AllowedHttpMethods.get,
    params: {
      fileName,
      tenantKey,
      token,
    },
    responseType: 'blob',
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const uploadBulkOfCandidates = async (): Promise<Blob | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.uploadBulkOfCandidates,
    method: AllowedHttpMethods.get,
    responseType: 'blob',
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const exportCandidateList = async (
  props: exportCandidateListProps,
): Promise<Blob | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.exportCandidateList,
    method: AllowedHttpMethods.get,
    params: {
      selectionStatus: withDefault(props?.selectionStatus, emptyString),
      fromDate: withDefault(props?.fromDate, emptyString),
      toDate: withDefault(props?.toDate, emptyString),
      searchByCandidateName: withDefault(
        props?.searchByCandidateName,
        emptyString,
      ),
      searchBySourceName: withDefault(props?.searchBySourceName, emptyString),
      searchByMultipleFlag: withDefault(
        props?.searchByMultipleFlag,
        emptyString,
      ),
      candidateStatus: withDefault(props?.candidateStatus, 'All'),
      selectionTechnology: withDefault(props?.selectionTechnology, emptyString),
      searchByExperience: withDefault(props?.searchByExperience, emptyString),
      selectionCountry: withDefault(props?.selectionCountry, emptyString),
      searchByRecruiterName: withDefault(props?.recruiter, emptyString),
      recruiter: withDefault(props?.recruiter, emptyString),
      experience: withDefault(props?.experience, emptyString),
      interviewRound: withDefault(props?.interviewRound, emptyString),
      isResumeMatcher: withDefault(props?.isResumeMatcher, emptyString),
      isExternalCreated: withDefault(props?.isExternalCreated, 'All'),
      appliedFor: withDefault(props?.appliedFor, emptyString),
    },
    responseType: 'blob',
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const exportInterviewCandidatesList = async (
  props: InterviewCandidateListConfigType,
): Promise<Blob | undefined> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.exportInterviewCandidatesList,
    method: AllowedHttpMethods.get,
    params: {
      selectionStatus: props?.selectionStatus || '',
      fromDate: props?.fromDate || '',
      toDate: props?.toDate || '',

      searchByCandidateName: props?.searchByCandidateName || '',
      searchByRecruiterName: props?.searchByRecruiterName || '',
      searchBySourceName: props?.searchBySourceName || '',
      searchByMultipleFlag: props?.searchByMultipleFlag || '',

      candidateStatus: props?.candidateStatus || '',
      selectionTechnology: props?.selectionTechnology || '',

      searchByExperience: props?.searchByExperience || '',

      selectionCountry: props?.selectionCountry || '',

      recruiter: props?.recruiter || '',
      experience: props?.experience || '',

      interviewRound: props?.interviewRound || '',
      isResumeMatcher: props?.isResumeMatcher || '',
      isExternalCreated: props?.isExternalCreated,
    },
    responseType: 'blob',
  })

  const response = await useAxios(requestConfig)
  return response.data
}

const getRecruitersDetails = async (): Promise<recruitersDetails[]> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.recritersDetails,
    method: AllowedHttpMethods.get,
  })
  const response = await useAxios(requestConfig)
  return response.data
}

const getInterviewCandidatesList = async (
  body: GetInterviewCandidateListType,
): Promise<InterviewCandidateListResponseType> => {
  const requestConfig = getAuthenticatedRequestConfig({
    url: CandidateListApiConfig.getInterviewCandidatesList,
    method: AllowedHttpMethods.get,
    additionalHeaders: {
      'Content-Type': 'application/json',
    },
    params: { ...body },
    // params: { ...body }
  })
  const response = await useAxios(requestConfig)
  return response.data
}

// const resumeCandidateList = async (
//   prepareObject: UploadResume,
// ): Promise<number | undefined> => {
//   const requestConfig = getAuthenticatedRequestConfig({
//     url: CandidateListApiConfig.resumeCandidateList,
//     method: AllowedHttpMethods.post,
//     data: prepareObject.file,
//     additionalHeaders: {
//       'Content-Type': 'multipart/form-data',
//     },
//   })
//   const response = await useAxios(requestConfig)
//   return response.data
// }

const resumeCandidateList = async (prepareObject: {
  file: FormData
}): Promise<AxiosResponse<UploadResumeType>> => {
  const token = 'bc9091fb-3371-483f-879b-c43a2dcd3264' // AI resume service token

  // Use axios directly (not useAxios) so a 401 from the AI service
  // does not trigger the app-wide logout that useAxios performs.
  // Content-Type is intentionally omitted so axios sets the correct
  // multipart/form-data boundary automatically for FormData.
  // Return the full axios response (not just response.data) so that
  // callers accessing response.payload.data.parsedResume keep working.
  // eslint-disable-next-line no-return-await
  return await axios({
    url: CandidateListApiConfig.resumeCandidateList,
    method: AllowedHttpMethods.post,
    data: prepareObject.file,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
}

export const candidateListApi = {
  searchScheduledCandidate,
  getEmpCountries,
  getTechnology,
  getCountryWiseCandidatesList,
  deleteCandidate,
  getAllJobVacancies,
  getAllEmployeeDetails,
  checkCandidateEmail,
  checkCandidateMobileNumber,
  getCandidateIdByEmail,
  getCandidateIdByPhone,
  getAllCompaniesData,
  addNewCandidate,
  uploadCandidateResume,
  addTechnology,
  deleteTechnology,
  editCandidateData,
  isEditNewCandidateEmailExists,
  isEditNewCandidateMobileNumExists,
  editNewCandidate,
  downloadFile,
  uploadBulkOfCandidates,
  exportCandidateList,
  getRecruitersDetails,
  getInterviewCandidatesList,
  exportInterviewCandidatesList,
  resumeCandidateList,
  getStatus,
}

export default candidateListApi
